import { ChevronLeft, PanelLeft } from 'lucide-react';
import * as React from 'react';

import { BUTTON_VARIANT } from '../types/button-variants-enums';
import { Button } from '@/components/ui/button';
import { SHEET_APPEARANCE, SHEET_SIDE } from '../types/sheet-variants-enums';
import { SIDEBAR_COLLAPSIBLE, SIDEBAR_SIDE, SIDEBAR_VARIANT } from './types/sidebar-enums';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { SIDEBAR_WIDTH_MOBILE } from './constants/sidebar-provider-variants-constants';
import { useSidebar } from './hooks/use-sidebar';
import type { SidebarProps } from './types/sidebar-core-types';

export const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  (
    {
      side = SIDEBAR_SIDE.LEFT,
      variant = 'sidebar',
      collapsible = SIDEBAR_COLLAPSIBLE.OFFCANVAS,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

    if (collapsible === 'none') {
      return (
        <div
          className={cn(
            'bg-surface-navigation text-content-primary flex h-full w-(--sidebar-width) flex-col',
            className
          )}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      );
    }

    if (isMobile) {
      return (
        <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
          <SheetContent
            appearance={SHEET_APPEARANCE.FOUNDATION}
            data-sidebar="sidebar"
            data-mobile="true"
            className="bg-surface-navigation text-content-primary w-(--sidebar-width) p-0 [&>button]:hidden"
            style={
              {
                '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
              } as React.CSSProperties
            }
            side={side === SIDEBAR_SIDE.RIGHT ? SHEET_SIDE.RIGHT : SHEET_SIDE.LEFT}
          >
            <div className="flex h-full w-full flex-col">{children}</div>
          </SheetContent>
        </Sheet>
      );
    }

    return (
      <div
        ref={ref}
        className="text-content-primary group peer hidden md:block"
        data-state={state}
        data-collapsible={state === 'collapsed' ? collapsible : ''}
        data-variant={variant}
        data-side={side}
      >
        <div
          className={cn(
            'relative h-svh w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear motion-reduce:transition-none',
            'group-data-[collapsible=offcanvas]:w-0',
            'group-data-[side=right]:rotate-180',
            variant === SIDEBAR_VARIANT.FLOATING || variant === SIDEBAR_VARIANT.INSET
              ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
              : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)'
          )}
        />
        <div
          className={cn(
            'fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear motion-reduce:transition-none md:flex',
            side === SIDEBAR_SIDE.LEFT
              ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
              : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
            variant === SIDEBAR_VARIANT.FLOATING || variant === SIDEBAR_VARIANT.INSET
              ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
              : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l',
            className
          )}
          {...props}
        >
          <div
            data-sidebar="sidebar"
            className="bg-surface-navigation group-data-[variant=floating]:border-border flex h-full w-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow"
          >
            {children}
          </div>
        </div>
      </div>
    );
  }
);
Sidebar.displayName = 'Sidebar';

export const SidebarTrigger = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentProps<typeof Button>
>(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      ref={ref}
      data-sidebar="trigger"
      variant={BUTTON_VARIANT.GHOST}
      size="icon"
      className={cn('h-8 w-8', className)}
      onClick={event => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeft />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
});
SidebarTrigger.displayName = 'SidebarTrigger';

export const SidebarRail = React.forwardRef<HTMLButtonElement, React.ComponentProps<'button'>>(
  ({ className, onClick, ...props }, ref) => {
    const { toggleSidebar, state } = useSidebar();
    const isCollapsed = state === 'collapsed';

    const handleToggle = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        toggleSidebar();
      },
      [onClick, toggleSidebar]
    );

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            ref={ref}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-sidebar="rail-toggle"
            onClick={handleToggle}
            type="button"
            className={cn(
              'group/rail absolute top-1/2 z-50 hidden -translate-y-1/2 md:flex',
              'h-8 w-8 items-center justify-center rounded-md',
              'pointer-coarse:min-h-11 pointer-coarse:min-w-11',
              'cursor-pointer bg-transparent',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-1',
              // Static gap past the border — no hover nudge
              'group-data-[side=left]:right-0 group-data-[side=left]:translate-x-[calc(50%+4px)]',
              'group-data-[side=right]:left-0 group-data-[side=right]:-translate-x-[calc(50%+4px)]',
              className
            )}
            {...props}
          >
            <span
              aria-hidden="true"
              data-sidebar="rail-handle"
              className={cn(
                'absolute h-7 w-0.5 rounded-full bg-action-primary',
                'opacity-100 transition-opacity duration-fast ease-standard',
                'group-hover/rail:opacity-0 group-focus-visible/rail:opacity-0',
                'pointer-coarse:opacity-0',
                'motion-reduce:transition-none'
              )}
            />
            <ChevronLeft
              aria-hidden="true"
              data-sidebar="rail-chevron"
              className={cn(
                'absolute h-4 w-4 text-content-secondary',
                // Slide slightly from the edge while fading in (GPU-only)
                'translate-x-0.5 opacity-0',
                'transition-[opacity,transform,color] duration-fast ease-standard',
                'group-hover/rail:translate-x-0 group-hover/rail:opacity-100 group-hover/rail:text-content-primary',
                'group-focus-visible/rail:translate-x-0 group-focus-visible/rail:opacity-100 group-focus-visible/rail:text-content-primary',
                'pointer-coarse:translate-x-0 pointer-coarse:opacity-100 pointer-coarse:text-content-primary',
                isCollapsed && 'rotate-180',
                'motion-reduce:transition-none'
              )}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          <span className="ml-2 text-xs text-content-secondary">⌘B</span>
        </TooltipContent>
      </Tooltip>
    );
  }
);
SidebarRail.displayName = 'SidebarRail';
